import { fetchWithAuth, BASE_URL } from "../api/client";

/**
 * Alpamayo Lab service — wraps the 16 routes documented by backend
 * (`/api/lab/*`). Mirrors the contract exactly: nothing is invented
 * client-side, no field names are renamed.
 *
 * Status vocabularies live in two namespaces:
 *   - Inbox proposal status: pending | in_flight | done | failed | rejected
 *   - Step Functions run status: RUNNING | SUCCEEDED | FAILED | TIMED_OUT | ABORTED
 *
 * The UI normalizes both into a single lowercase set
 * (queued | running | done | failed | rejected | cancelled) via
 * `normalizeRunStatus` / `normalizeInboxStatus` so existing pill styling
 * keeps working.
 */

const LAB_BASE = `${BASE_URL}/api/lab`;
const jsonHeaders = { "Content-Type": "application/json" };

const parseError = async (response, fallback) => {
    let body = null;
    try {
        body = await response.json();
    } catch (_) { /* not JSON */ }
    const err = new Error(body?.detail || body?.error || body?.message || fallback);
    err.status = response.status;
    err.body = body;
    err.notDeployed = response.status === 404;
    return err;
};

const requireOk = async (response, fallback) => {
    if (!response.ok) throw await parseError(response, fallback);
    // 204 / empty body
    const text = await response.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch (_) { return text; }
};

/** True when the backend Lab API is not reachable (404 / network / 5xx).
 *
 * Returns FALSE for auth-related failures (401, 403, refresh-token expired
 * — tagged via `error.authExpired`), even though refresh-token failures
 * throw without a `.status` field. Those need their own UX (redirect to
 * login or upgrade gate), not the "preview mode" banner.
 */
export const isBackendUnavailable = (error) => {
    if (!error) return false;
    if (error.authExpired) return false;       // session expired ≠ backend down
    if (error.status === 401) return false;    // unauthenticated ≠ backend down
    if (error.status === 403) return false;    // tier/permission ≠ backend down
    if (error.notDeployed) return true;
    if (error.status === 404) return true;
    if (error.status >= 500) return true;
    if (!error.status) return true;
    return false;
};

/** True when the error means "user needs to log in again." */
export const isAuthExpired = (error) =>
    Boolean(error && (error.authExpired || error.status === 401));

// ── Tier gate ───────────────────────────────────────────────────────────────
// Backend doc lists {pro, business, ultra, enterprise}, but product wants
// Lab restricted to Ultra+ only — Pro/Business hit the upgrade splash.
export const ALLOWED_TIERS = ["ultra", "enterprise"];

// ── Status normalization ────────────────────────────────────────────────────
const RUN_STATUS_MAP = {
    RUNNING:   "running",
    SUCCEEDED: "done",
    FAILED:    "failed",
    TIMED_OUT: "failed",
    ABORTED:   "cancelled",
};
const INBOX_STATUS_MAP = {
    pending:   "queued",
    in_flight: "running",
    done:      "done",
    failed:    "failed",
    rejected:  "rejected",
};
export const normalizeRunStatus = (s) => RUN_STATUS_MAP[s] || (s ? s.toLowerCase() : "queued");
export const normalizeInboxStatus = (s) => INBOX_STATUS_MAP[s] || s || "queued";

export const STATUS_META = {
    queued:    { label: "Queued",    dot: "bg-slate-400",     pill: "bg-slate-100 text-slate-700 border-slate-200" },
    running:   { label: "Running",   dot: "bg-blue-500 animate-pulse", pill: "bg-blue-50 text-blue-700 border-blue-200" },
    done:      { label: "Done",      dot: "bg-emerald-500",   pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed:    { label: "Failed",    dot: "bg-red-500",       pill: "bg-red-50 text-red-700 border-red-200" },
    rejected:  { label: "Rejected",  dot: "bg-amber-500",     pill: "bg-amber-50 text-amber-700 border-amber-200" },
    cancelled: { label: "Cancelled", dot: "bg-slate-400",     pill: "bg-slate-100 text-slate-600 border-slate-200" },
};

// The 4 Step-Function states the pipeline surfaces (see backend §4.6 + spec §4.4.2).
export const PIPELINE_STATES = [
    { name: "ClaimInbox",          label: "Claim Inbox" },
    { name: "GenerateSweepConfig", label: "Generate Sweep Config" },
    { name: "RunSweep",            label: "Run Sweep" },
    { name: "Finalise",            label: "Finalise" },
];

// ── Health ──────────────────────────────────────────────────────────────────
export const fetchHealth = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/health`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load Lab health");
};

// ── Inbox (proposals) ───────────────────────────────────────────────────────
/** status: pending|in_flight|done|failed|rejected. limit ≤ 50 server-side. */
export const fetchInbox = async ({ status = "pending", limit = 50 } = {}) => {
    const params = new URLSearchParams({ status, limit: String(limit) });
    const r = await fetchWithAuth(`${LAB_BASE}/inbox?${params}`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load inbox");
};

export const fetchInboxItem = async (proposalId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/inbox/${encodeURIComponent(proposalId)}`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load proposal");
};

/** Body: { proposal_id?: string, yaml_content: string }. Returns 201. */
export const submitInbox = async ({ yamlContent, proposalId }) => {
    const r = await fetchWithAuth(`${LAB_BASE}/inbox`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ yaml_content: yamlContent, ...(proposalId ? { proposal_id: proposalId } : {}) }),
    });
    return requireOk(r, "Failed to submit proposal");
};

export const deleteInboxItem = async (proposalId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/inbox/${encodeURIComponent(proposalId)}`, { method: "DELETE", headers: jsonHeaders });
    return requireOk(r, "Failed to withdraw proposal");
};

// ── Runs (Step Function executions) ─────────────────────────────────────────
/** status: RUNNING|SUCCEEDED|FAILED|ABORTED|TIMED_OUT (or omit for all). */
export const fetchRuns = async ({ status, limit = 50 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.append("status", status);
    const r = await fetchWithAuth(`${LAB_BASE}/runs?${params}`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load runs");
};

export const fetchRun = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load run");
};

export const stopRun = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}:stop`, { method: "POST", headers: jsonHeaders });
    return requireOk(r, "Failed to stop run");
};

/** Fires one execution that claims the next pending inbox item. */
export const triggerRun = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs:trigger`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ plan_on_empty: false }),
    });
    return requireOk(r, "Failed to trigger run");
};

// ── Reports ─────────────────────────────────────────────────────────────────
export const fetchRunReport = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}/report`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load report");
};

export const fetchRunReportMarkdown = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}/report.md`, {
        headers: { Accept: "text/markdown" },
    });
    if (!r.ok) throw await parseError(r, "Failed to load report markdown");
    return r.text();
};

export const fetchExperimentRaw = async (runId, expName, { limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    const r = await fetchWithAuth(
        `${LAB_BASE}/runs/${encodeURIComponent(runId)}/experiments/${encodeURIComponent(expName)}/raw?${params}`,
        { headers: jsonHeaders }
    );
    return requireOk(r, "Failed to load raw results");
};

export const fetchRunRecommendations = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}/recommendations`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load recommendations");
};

/**
 * Wedge 2: Reasoning-action consistency audit.
 *
 * Returns the aggregated audit (decision-groundedness rate, reasoning-action
 * consistency rate, longitudinal/lateral consistency breakdown, plus the
 * full list of consistency-failure cases) for one run. Each invocation's CoT
 * was parsed into the 15-decision vocabulary from NVIDIA's Alpamayo-R1 paper
 * (Table 1) and matched against what the trajectory actually did.
 *
 * Customers (regulators, insurers, safety teams) use this to find scenarios
 * where the model says one thing and does another — the audit-worthy cases.
 *
 * Returns 404 until the run completes and the audit artifact is uploaded.
 */
export const fetchRunAudit = async (runId) => {
    const r = await fetchWithAuth(`${LAB_BASE}/runs/${encodeURIComponent(runId)}/audit`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load reasoning audit");
};

// ── Scenario video (audit UI) ───────────────────────────────────────────────
/**
 * Returns a presigned URL for the ~13s MP4 around t0 for one camera of a
 * scenario. Used in the Audit tab's video modal so a customer can watch the
 * scene play out around the moment the model was evaluated.
 *
 * Cameras: front_wide | front_tele | cross_left | cross_right
 * URL is valid for 5 minutes — re-fetch if it expires.
 *
 * Returns 404 if the scenario was imported without --include-video. The UI
 * should gracefully fall back to showing only the 4 still frames + trajectory plot.
 */
export const fetchScenarioVideo = async (scenarioId, camera = "front_wide") => {
    const params = new URLSearchParams({ camera });
    const r = await fetchWithAuth(
        `${LAB_BASE}/scenarios/${encodeURIComponent(scenarioId)}/video?${params}`,
        { headers: jsonHeaders }
    );
    return requireOk(r, "Failed to load scenario video URL");
};

export const fetchScenarioMeta = async (scenarioId) => {
    const r = await fetchWithAuth(
        `${LAB_BASE}/scenarios/${encodeURIComponent(scenarioId)}/meta`,
        { headers: jsonHeaders }
    );
    return requireOk(r, "Failed to load scenario meta");
};

// ── Closed-loop (AlpaSim) ────────────────────────────────────────────────
/**
 * Trigger a closed-loop simulation run via NVIDIA's open-source AlpaSim.
 *
 * The customer picks scenes (clipgt-* IDs) to evaluate in closed-loop.
 * Returns 202 with a run_id immediately — actual simulation takes
 * minutes to hours depending on scene count. Poll fetchClosedLoopReport.
 */
export const triggerClosedLoopRun = async ({
    driver = "alpamayo1_5",
    topology = "1gpu",
    sceneIds = [],
    rolloutsPerScene = 1,
    useOurEndpoint = true,
} = {}) => {
    const r = await fetchWithAuth(`${LAB_BASE}/closed-loop/run`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
            driver,
            topology,
            scene_ids: sceneIds,
            rollouts_per_scene: rolloutsPerScene,
            use_our_endpoint: useOurEndpoint,
        }),
    });
    return requireOk(r, "Failed to trigger closed-loop run");
};

/**
 * Fetch the closed-loop AlpaSim report for a run.
 *
 * Returns: {
 *   run_id,
 *   status: "queued" | "running" | "complete" | "failed",
 *   metrics: { collision_at_fault_rate, offroad_rate, dist_to_gt_trajectory_mean, ... },
 *   violation_videos: [{ type, scene_id, filename, video_url }, ...],
 *   aggregate_report_url
 * }
 */
export const fetchClosedLoopReport = async (runId) => {
    const r = await fetchWithAuth(
        `${LAB_BASE}/runs/${encodeURIComponent(runId)}/closed-loop`,
        { headers: jsonHeaders }
    );
    return requireOk(r, "Failed to load closed-loop report");
};

// ── Endpoint state ──────────────────────────────────────────────────────────
export const fetchEndpoint = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/endpoint`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load endpoint state");
};

// ── Pipeline controls ───────────────────────────────────────────────────────
export const pausePipeline = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/control/pause`, { method: "POST", headers: jsonHeaders });
    return requireOk(r, "Failed to pause pipeline");
};

export const resumePipeline = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/control/resume`, { method: "POST", headers: jsonHeaders });
    return requireOk(r, "Failed to resume pipeline");
};

export const fetchPipelineState = async () => {
    const r = await fetchWithAuth(`${LAB_BASE}/control/state`, { headers: jsonHeaders });
    return requireOk(r, "Failed to load pipeline state");
};

// ── Live events (SSE — wraps EventSource, no fetchWithAuth) ─────────────────
/**
 * Returns the EventSource so the caller can attach listeners and close it.
 * Server closes on terminal status (event: "terminal").
 */
export const openRunEventStream = (runId) => {
    const url = `${LAB_BASE}/runs/${encodeURIComponent(runId)}/events`;
    return new EventSource(url, { withCredentials: true });
};

// ── Default YAML template (spec §8) ─────────────────────────────────────────
export const DEFAULT_PROPOSAL_YAML = `id: my-temperature-sweep
title: Temperature sensitivity sweep
priority: medium
status: queued
created_at: ${new Date().toISOString().slice(0, 10)}
hypothesis: Lower T should reduce tail error.
background: Submitted via Lab.
verification:
  primary_metric: min_ade_mean
  direction: lower
  guardrails:
    - "latency_p95_seconds <= baseline.latency_p95_seconds * 1.25"
    - "successes / n == 1.0"
modification_surface:
  type: decoding_sweep
  varies: [temperature]
  fixed:
    top_p: 0.98
    num_traj_samples: 6
    max_generation_length: 256
search_space:
  temperature: [0.0, 0.2, 0.4, 0.6, 0.8, 1.0]
scenario_set:
  source: local_dir
  name_or_path: experiments/alpamayo/data/scenarios
  count: 50
  selection: first_n
cost_estimate:
  gpu_minutes: 75
  usd: 2.50
scope_guardrails:
  - "DO NOT modify Python source. Configs only."
success_criteria:
  - "At least one temperature setting produces min_ade_mean below baseline."
gemma_instructions: |
  Produce one entry per value of search_space.temperature.
  Each entry sets the temperature; top_p / num_traj_samples /
  max_generation_length come from modification_surface.fixed.
`;
